import format from 'string-template';
import { Collection, getFirestore, getFirebase } from 'firestorter';

import moment from 'moment';
import { firestorePaths, pathPlaceholder } from '../utils/paths';
import {
  findLiveContractForProduct,
  getActiveProductIds,
  getContractEntries,
  getContractEntryAtDate,
  getContractEntryById,
  getContractView,
  getFirstServiceStartAt,
  getSoleContractEntry,
  hasMultipleActiveProducts,
  isFutureStartForAllProducts,
  isOnBreakForAllProducts,
  reportMissingCoachData,
  resolvePrimaryCoachId,
} from '../utils/userCoachData';
import { DEFAULT_TAG } from '../CoachTools/Model/AppCustomCheckin';
import { ClientStatus } from '../CoachTools/utils/statusFilter';
import { getUserStatus } from '../CoachTools/utils/userStatus';
import { DateFormat } from '../utils/date';
import BaseDocument from './BaseDocument';

/**
 * User roles.
 *
 * These values are compared against the `roles` array on the user document, which the Roles admin
 * page fills from the `name` of a `role` collection document. They must match those names exactly
 * — `Support`, not `SUPPORT` — or a role reads as not held. The server's
 * `ROLE_FLAG_TO_PERMISSION_ROLE` writes the same spellings.
 */
const UserRole = {
  CRX: 'CRX',
  IS: 'IS',
  SDR: 'SDR',
  SUPPORT: 'Support',
  AC: 'AC',
  CAM: 'CAM',
  CPS: 'CPS',
};

/**
 * User category.
 * This is used to determine the user type in the coach tools.
 */
const UserCategory = {
  AI_FRIEND: 'AI_FRIEND',
  CHALLENGES: 'CHALLENGES',
  FREE_CLIENTS: 'FREE_CLIENTS',
  OTHER: 'OTHER',
};

/**
 * User role flags.
 * This is used to query users by role.
 */
const UserRoleFlag = {
  [UserRole.CRX]: 'isCRX',
  [UserRole.IS]: 'isInsideSales',
  [UserRole.SDR]: 'isSDR',
  [UserRole.SUPPORT]: 'isSupport',
  [UserRole.AC]: 'isCoachAssistant',
  [UserRole.CAM]: 'isCAM',
  [UserRole.CPS]: 'isCPS',
};

/**
 * Class representing a User.
 *
 * @class User
 * @extends BaseDocument
 */
class User extends BaseDocument {
  /**
   * @param {string|Function} source - The firestore document path.
   * @param {object} [opts] - firestorter Document options, plus:
   * @param {string} [opts.viewAsCoachId] - Coach whose relationship this doc
   * is viewed as. Passed by the coach-scoped query builders; when unset,
   * reads fall back to the user's ONLY coach — for multi-coach users no
   * fallback is applied (per-coach reads come back empty rather than
   * guessing and leaking another coach's data).
   */
  constructor(source, opts = {}) {
    const { viewAsCoachId, ...documentOpts } = opts;
    super(source, documentOpts);
    this.viewAsCoachId = viewAsCoachId;
  }

  /**
   * The coach id all per-coach reads are scoped to.
   * @return {string|undefined}
   */
  get resolvedCoachId() {
    return this.viewAsCoachId || resolvePrimaryCoachId(this.data);
  }

  /**
   * Reports (once per user+coach per session) a loaded user doc missing its
   * `coachData.{coachId}` entry for an explicitly scoped coach — a backfill gap.
   * @param {string} userId - The user doc id.
   * @param {string} coachId - The explicitly scoped coach id.
   */
  static reportMissingCoachData(userId, coachId) {
    reportMissingCoachData(userId, coachId);
  }

  /**
   * Per-coach state (`coachData.{coachId}`) for the coach this doc is
   * viewed as.
   * @return {object}
   */
  get coachData() {
    const entry = this.resolvedCoachId ? this.data.coachData?.[this.resolvedCoachId] : undefined;
    if (!entry && this.viewAsCoachId && this.exists) {
      User.reportMissingCoachData(this.id, this.viewAsCoachId);
    }
    return entry || {};
  }

  /**
   * The best contract (active first, then best status, then latest started)
   * among the `contracts` map entries of the coach this doc is viewed as —
   * the single contract the UI renders for the relationship.
   * @return {object}
   */
  get contractView() {
    return getContractView(this.data.contracts, this.resolvedCoachId);
  }

  /**
   * One entry by id whatever coach it names, for callers that have established
   * ownership another way — a contract doc of this coach, say.
   * @param {string} contractId - The contract to read.
   * @return {object|undefined}
   */
  contractEntryById(contractId) {
    return getContractEntryById(this.data.contracts, contractId);
  }

  /**
   * The subscription episode this client was in on a given date — what anything
   * dated should read instead of `contractView`, which answers "current best
   * contract".
   *
   * Whenever the coach-scoped lookup finds nothing, falls back to the doc's sole
   * contract: that covers an unresolvable coach (ambiguous multi-coach docs, and
   * reassigned clients whose `coachData` entry names a different coach than their
   * `contracts` entry) and equally a coach with no episode that old. One contract
   * is unambiguous whoever it names; several without a coach are not.
   *
   * Undated in, undated out — with no `at` there is no episode to name, and the
   * sole contract would be an answer to a question nobody asked.
   * @param {Date} at - The moment to date against.
   * @return {object|undefined} The contract entry, with its `contractId`.
   */
  contractEntryAtDate(at) {
    if (!at) {
      return undefined;
    }
    return getContractEntryAtDate(this.data.contracts, this.resolvedCoachId, at)
      || getSoleContractEntry(this.data.contracts);
  }

  /**
   * Every contract entry of the coach this doc is viewed as.
   * @return {Array<object>}
   */
  get contractEntries() {
    return getContractEntries(this.data.contracts, this.resolvedCoachId);
  }

  /**
   * The client as seen through one contract, in the shape the status checks in
   * `CoachTools/utils/statusFilter` read — so `isActive` and `subscriptionStatus`
   * come from the contract rather than the coach-level rollup.
   * @param {string} contractId - The contract to view through.
   * @return {object|null} The view, or null when this coach has no such contract.
   */
  contractStatusView(contractId) {
    const entry = getContractEntryById(this.data.contracts, contractId);
    if (!entry) {
      return null;
    }
    return {
      id: this.id,
      name: this.name,
      contractId: entry.contractId,
      productId: entry.productId,
      isActive: !!entry.isActive,
      subscriptionStatus: entry.subscriptionStatus,
      serviceStartAt: entry.serviceStartAt?.toDate(),
      breakEndDate: entry.breakEndDate?.toDate(),
      cancelAt: entry.cancelAt,
      canceledAt: entry.canceledAt,
      monthlyStartAt: entry.monthlyStartAt,
      amendingAt: entry.amendingAt,
      // Per-coach rather than per-contract, so the client-level value is the only one there is.
      isDelinquent: this.isDelinquent,
      postPaymentFormSubmittedAt: this.postPaymentFormSubmittedAt,
    };
  }

  /**
   * Distinct product ids the client holds live with the coach this doc is
   * viewed as. Product names are not on the contract entry — read them from the
   * matching `userContract` docs, which the billing views already load.
   * @return {Array<string>}
   */
  get activeProductIds() {
    return getActiveProductIds(this.data.contracts, this.resolvedCoachId);
  }

  /**
   * Whether the client holds more than one live product with this coach — the
   * single gate for showing a product or contract selector.
   * @return {boolean}
   */
  get hasMultipleActiveProducts() {
    return hasMultipleActiveProducts(this.data.contracts, this.resolvedCoachId);
  }

  /**
   * Every product the client's feature access can come from: their live products,
   * plus the contract view's product so an unmigrated doc still resolves to one.
   * @return {Array<string>}
   */
  get featureProductIds() {
    return [...new Set([...this.activeProductIds, this.product].filter(Boolean))];
  }

  /**
   * Whether every live product is on a break — the client-level answer, so one
   * paused product does not read as a paused client.
   * @return {boolean}
   */
  get isOnBreakForAllProducts() {
    return isOnBreakForAllProducts(this.data.contracts, this.resolvedCoachId);
  }

  /**
   * Whether no live product has started yet.
   * @return {boolean}
   */
  get isFutureStartForAllProducts() {
    return isFutureStartForAllProducts(this.data.contracts, this.resolvedCoachId);
  }

  /**
   * Whether this client already holds a live contract with the given coach for the
   * given product. Used by the onboarding flow to tell a genuine additional purchase
   * (a different product from a coach they already have — allowed) from a repeat of
   * something they are already paying for (blocked).
   *
   * Takes an explicit coach id rather than using `resolvedCoachId`, because during
   * onboarding the coach comes from the contract being bought, not from the doc.
   *
   * Returns the entry (not a boolean) because its `contractId` is what links through to
   * the matching Stripe subscription.
   *
   * @param {string} coachId - The coach being bought from.
   * @param {string} productId - The product being bought.
   * @return {object|undefined} `{ contractId, ...entry }` when one is live.
   */
  getLiveContractForProduct(coachId, productId) {
    return findLiveContractForProduct(this.data.contracts, coachId, productId);
  }

  /**
   * Whether this doc carries a `contracts` map at all. Without one no per-contract question
   * has an answer, so callers must not read "no entry for this product" as "nothing live".
   * @return {boolean}
   */
  get hasContractEntries() {
    return !!Object.keys(this.data.contracts || {}).length;
  }

  /**
   * Every coach the user has ever subscribed to.
   * @return {Array<String>}
   */
  get assignedCoaches() {
    return this.data.assignedCoaches || [];
  }

  /**
   * Every coach the client has ever held, from the rollup and the per-coach map. It never shrinks,
   * so an ended relationship stays in it — which is what the per-coach documents are keyed by.
   * @return {string[]}
   */
  get coachIds() {
    const assigned = this.assignedCoaches;
    const mapKeys = Object.keys(this.data.coachData || {});
    const union = [...assigned, ...mapKeys.filter((id) => !assigned.includes(id))];
    if (union.length) {
      return union;
    }
    return this.data.assignedCoach ? [this.data.assignedCoach] : [];
  }

  /**
   * The coaches the client holds right now, as opposed to `coachIds`, which never shrinks.
   * @return {string[]}
   */
  get activeCoachIds() {
    return this.coachIds.filter((coachId) => !!this.data.coachData?.[coachId]?.flags?.isActive);
  }

  /**
     * Get user's address.
     * @return {string}
     */
  get address() {
    return this.data.address;
  }

  /**
   * Get user's amending at date in unix timestamp.
   * @return {number}
   * Note: the legacy root twin of this field is deprecated for web — do not read it from `this.data`.
   */
  get amendingAt() {
    return this.contractView.amendingAt;
  }

  /**
   * Get the id of user's assigned coach — the coach this doc is viewed as.
   * Derived from the maps; the root `assignedCoach` twin is fill-only legacy
   * for old mobile builds.
   * @return {string}
   */
  get assignedCoach() {
    return this.resolvedCoachId;
  }

  /**
     * Get user's avatar link.
     * @return {string}
     */
  get avatarUrl() {
    return this.data.avatarUrl;
  }

  /**
   * Get user's break end date.
   * @return {Date}
   * Note: the legacy root twin of this field is deprecated for web — do not read it from `this.data`.
   */
  get breakEndDate() {
    return this.contractView.breakEndDate?.toDate();
  }

  /**
   * Get user's birthdate.
   * @return {Date|void}
   */
  get birthdate() {
    return this.data.birthdate?.toDate();
  }

  /**
   * Get user's cancel at date in unix timestamp.
   * @return {number}
   * Note: the legacy root twin of this field is deprecated for web — do not read it from `this.data`.
   */
  get cancelAt() {
    return this.contractView.cancelAt;
  }

  /**
   * Get user's canceled at date.
   * @return {number}
   * Note: the legacy root twin of this field is deprecated for web — do not read it from `this.data`.
   */
  get canceledAt() {
    return this.contractView.canceledAt;
  }

  /**
   * Get user's check in day (day of the week).
   * @return {number}
   */
  get checkInDay() {
    return this.data.checkInDay;
  }

  /**
   * Get user's notes from the coach.
   * @return {string}
   * Note: the legacy root twin of this field is deprecated for web — do not read it from `this.data`.
   */
  get coachNotes() {
    return this.coachData.coachNotes || '';
  }

  /**
   * When coach notes were last saved (server time).
   * @return {Date}
   * Note: the legacy root twin of this field is deprecated for web — do not read it from `this.data`.
   */
  get coachNotesUpdatedAt() {
    return this.coachData.coachNotesUpdatedAt?.toDate?.();
  }

  /**
   * Display name of the user who last saved coach notes.
   * @return {string}
   * Note: the legacy root twin of this field is deprecated for web — do not read it from `this.data`.
   */
  get coachNotesUpdatedByName() {
    return this.coachData.coachNotesUpdatedByName || '';
  }

  // Note: the legacy root twin of this field is deprecated for web — do not read it from `this.data`.
  get workoutPreferences() {
    return this.coachData.workoutPreferences || '';
  }

  // Note: the legacy root twin of this field is deprecated for web — do not read it from `this.data`.
  get workoutPreferencesUpdatedAt() {
    return this.coachData.workoutPreferencesUpdatedAt?.toDate?.();
  }

  // Note: the legacy root twin of this field is deprecated for web — do not read it from `this.data`.
  get workoutPreferencesUpdatedByName() {
    return this.coachData.workoutPreferencesUpdatedByName || '';
  }

  /**
   * Get user's custom permissions for various features.
   * @return {Object} An object mapping feature keys to an array of permissions.
   */
  get customPermissions() {
    return this.data?.customPermissions || {};
  }

  // Note: the legacy root twin of this field is deprecated for web — do not read it from `this.data`.
  get customTags() {
    return this.coachData.customTags || [];
  }

  /**
   * The tag of the check-in template the coach chose for this client, if any.
   * @return {string|undefined}
   */
  get checkInTemplateTag() {
    return this.coachData.checkInTemplateTag;
  }

  get communityIds() {
    return this.data.communityIds || [];
  }

  /**
   * Get the coach's community access (default and frozen communities), or null when absent.
   * @param {string} coachId - The coach whose access to read.
   * @return {{defaultCommunityId: (string|null), frozen: Object}|null}
   */
  communityAccessFor(coachId) {
    const access = coachId ? this.data.coachData?.[coachId]?.communityAccess : undefined;
    if (!access) {
      return null;
    }
    return {
      defaultCommunityId: access.defaultCommunityId || null,
      frozen: access.frozen || {},
    };
  }

  /**
   * Get user's commitment ending date.
   * @return {Date}
   * Note: the legacy root twin of this field is deprecated for web — do not read it from `this.data`.
   */
  get commitmentEndingDate() {
    return this.contractView.commitmentEndingDate?.toDate();
  }

  /**
   * Get user's createdAt date.
   * @return {Date|void}
   */
  get createdAt() {
    return this.data.createdAt?.toDate();
  }

  /**
   * Get default meal plan id if the user is a coach.
   * @return {string}
   * Note: the legacy root twin of this field is deprecated for web — do not read it from `this.data`.
   */
  get defaultMealPlan() {
    return this.coachData.defaultMealPlan || '';
  }

  /**
   * Get user's email. Prefers baseEmail (the user's real email) when present —
   * secondary multi-coach accounts have a shifted `email` but their real address
   * lives in `baseEmail`.
   * @return {string}
   */
  get email() {
    return this.data.baseEmail || this.data.email;
  }

  /**
   * Get user's baseEmail if any. Only set on secondary multi-coach accounts,
   * where it holds the original (un-shifted) email.
   * @return {string|undefined}
   */
  get baseEmail() {
    return this.data.baseEmail;
  }

  /**
   * Get the email the account itself uses. On secondary multi-coach accounts this is the
   * shifted address, which differs from the real one exposed by `email`. Both are worth
   * looking a user up by, so prefer this over `email` only when you specifically need the
   * address tied to the account rather than to the person.
   * @return {string|undefined}
   */
  get accountEmail() {
    return this.data.email;
  }

  /**
   * if the user sent a chat message and is expecting a reply.
   * @return {boolean}
   * Note: the legacy root twin of this field is deprecated for web — do not read it from `this.data`.
   */
  get expectingReply() {
    return !!this.coachData.expectingReply;
  }

  /**
   * Get user's first name.
   * @return {string}
   */
  get firstName() {
    return this.data.firstName;
  }

  /**
   * Get user's flags.
   * @return {object} user flags.
   * @property {boolean} isActive - user's active status. This is true if user's subscription is trialing or active.
   * @property {boolean} dayZero - true until the user has logged in for the first time.
   * @property {boolean} delinquent - this maps to the Stripe customer delinquent field.
   * automatic payment failure or passing the invoice.due_date will set this field to true.
   * @property {string} subscriptionStatus - user's subscription status.
   * This maps to the Stripe subscription status (in uppercase).
   * @property {boolean} greetingMessageSent - reflects whether the greeting message has been sent to the user.
   * Defaults to false and is set to true after the greeting message is sent to the user.
   * @property {boolean} firstPaymentSuccessful - reflects whether the first invoice payment was successful
   *
   * Read from `coachData.{coachId}.flags` — the root `flags` twin is a
   * cross-coach rollup reserved for coach-agnostic queries.
   */
  get flags() {
    return this.coachData.flags || {};
  }

  /**
   * Get user's gender.
   * @return {string}
   */
  get gender() {
    return this.data.gender;
  }

  // Set by the mobile app while the client keeps cycle tracking on; coach views show no cycle data otherwise.
  get isCycleTrackingActive() {
    return !!this.data.isCycleTrackingActive;
  }

  /**
   * Get greeting message sent status.
   * This reflects whether the greeting message has been sent to the user.
   * Defaults to false and is set to true after the greeting message is sent to the user.
   * @return {boolean}
   * Note: the legacy root twin of this field is deprecated for web — do not read it from `this.data`.
   */
  get greetingMessageSent() {
    return !!this.flags.greetingMessageSent;
  }

  /**
   * Get user's active status.
   * This is true if user's subscription is trialing or active.
   * @return {boolean}
   * Note: the legacy root twin of this field is deprecated for web — do not read it from `this.data`.
   */
  get isActive() {
    return !!this.flags.isActive;
  }

  /**
   * If AI Friend is enabled for the user.
   * Root `flags` rollup read — intentionally outside the v2 coachData schema
   * (mobile keeps reading it from the root too).
   * @return {boolean}
   */
  get isAIFriendEnabled() {
    return !!this.data.flags?.isAIFriendEnabled;
  }

  /**
   * Whether the user holds the given role.
   *
   * A role is recorded in two places: the `roles` array, written by the Roles admin page, and the
   * per-role boolean flag that `getUsersByRole` queries on. The `updateUserPermissions` trigger
   * keeps the two in step in both directions, so under normal operation either one answers this.
   *
   * Reading both anyway is deliberate. That trigger's error handler logs and returns, so a failed
   * sync is silent; it fires on update only, so a document created with `roles` already set never
   * gets its flag; and there is a window between the two writes. In each of those the array is
   * right and the flag is not, and a gate reading the flag alone would hide a role the user holds.
   *
   * @param {string} role - A `UserRole` value.
   * @return {boolean}
   */
  hasRole(role) {
    return !!this.data[UserRoleFlag[role]] || this.roles.includes(role);
  }

  /**
   * If the user is an assistant coach.
   * @return {boolean}
   */
  get isCoachAssistant() {
    return this.hasRole(UserRole.AC);
  }

  get showACSenderNames() {
    return !!this.data.shouldShowSenderNamesOnChat;
  }

  updateShowACSenderNames = async (value) => {
    await this.updateFields({
      shouldShowSenderNamesOnChat: value,
    });
  };

  /**
   * Get user's day zero status.
   * This is true until the user has logged in for the first time.
   * @return {boolean}
   * Note: the legacy root twin of this field is deprecated for web — do not read it from `this.data`.
   */
  get isDayZero() {
    return !!this.flags.dayZero;
  }

  /**
   * Get user's delinquent status.
   * This maps to the Stripe customer delinquent field.
   * Automatic payment failure or passing the invoice.due_date will set this field to true.
   * @return {boolean}
   * Note: the legacy root twin of this field is deprecated for web — do not read it from `this.data`.
   */
  get isDelinquent() {
    return !!this.flags.delinquent;
  }

  /**
   * If the user is an inside sales agent.
   * @return {boolean}
   */
  get isInsideSales() {
    return this.hasRole(UserRole.IS);
  }

  /**
   * Get user's workout notification resume date.
   * @return {Date|void}
   * Note: the legacy root twin of this field is deprecated for web — do not read it from `this.data`.
   */
  get workoutNotificationsResumeDate() {
    return this.coachData.workoutNotificationsResumeDate?.toDate();
  }

  /**
   * Get user's meal plan notification resume date.
   * @return {Date|void}
   * Note: the legacy root twin of this field is deprecated for web — do not read it from `this.data`.
   */
  get mealPlanNotificationsResumeDate() {
    return this.coachData.mealPlanNotificationsResumeDate?.toDate();
  }

  /**
   * Get IS user enabled to instant calls.
   * @return {boolean}
   */
  get instaCallIS() {
    return !!this.data.instaCallIS;
  }

  /**
   * Return IS user is on-call or not.
   * @return {boolean}
   */
  get isISOnCall() {
    return !!this.data.isISOnCall;
  }

  /**
   * Get user's onboarded status.
   * Defaults to false and is set to true after a subscription is created.
   * If coach cancels the subscription, this flag is set to false.
   * @return {boolean}
   */
  get isOnboarded() {
    return !!this.data.isOnboarded;
  }

  /**
   * If the user is a sales development representative.
   * @return {boolean}
   */
  get isSDR() {
    return this.hasRole(UserRole.SDR);
  }

  /**
   * If the user is an on call engineer.
   * @return {boolean}
   */
  get isOnCall() {
    return !!this.data.isOnCall;
  }

  /**
   * If the user is a coach relations execute.
   * @return {boolean}
   */
  get isCRX() {
    return this.hasRole(UserRole.CRX);
  }

  /**
   * If the user is a support agent.
   * @return {boolean}
   */
  get isSupport() {
    return this.hasRole(UserRole.SUPPORT);
  }

  /**
   * If the user is a CAM.
   * @return {boolean}
   */
  get isCAM() {
    return this.hasRole(UserRole.CAM);
  }

  /**
   * If the user is a CPS.
   * @return {boolean}
   */
  get isCPS() {
    return this.hasRole(UserRole.CPS);
  }

  /**
   * Get user's last check in date.
   * @return {Date|void}
   */
  get lastCheckIn() {
    return this.data.lastCheckIn?.toDate();
  }

  /**
   * Get the date of the last message from anyone other than this coach — normally the client.
   * Never the coach's own message; see `lastCoachMessageAt` for that.
   * @return {Date|void}
   * Note: the legacy root twin of this field is deprecated for web — do not read it from `this.data`.
   */
  get lastMessageAt() {
    return this.coachData.lastMessageAt?.toDate();
  }

  /**
   * Get the date the coach last typed a 1:1 message to this client.
   * Broadcasts and automated sends are deliberately excluded.
   * @return {Date|void}
   */
  get lastCoachMessageAt() {
    return this.coachData.lastCoachMessageAt?.toDate();
  }

  /**
   * Get user's last name.
   * @return {string}
   */
  get lastName() {
    return this.data.lastName;
  }

  /**
   * Get user's last workout completed date.
   * @return {Date}
   * Note: the legacy root twin of this field is deprecated for web — do not read it from `this.data`.
   */
  get lastWorkoutCompletedAt() {
    return this.coachData.lastWorkoutCompletedAt?.toDate();
  }

  /**
   * Get user's broadcast silenced until date (applies to all broadcast types).
   * @return {Date|void}
   * Note: the legacy root twin of this field is deprecated for web — do not read it from `this.data`.
   */
  get disengagementBroadcastSilencedUntil() {
    return this.coachData.disengagementBroadcastSilencedUntil?.toDate();
  }

  /**
   * Get user's disengagement outreach reached at date.
   * @return {Date|void}
   * Note: the legacy root twin of this field is deprecated for web — do not read it from `this.data`.
   */
  get disengagementOutreachReachedAt() {
    return this.coachData.disengagementOutreachReachedAt?.toDate();
  }

  /**
   * Get user's monthly start date in unix timestamp.
   * If user has a PIF plan, this will be the date when the user's monthly subscription starts.
   * @return {number}
   * Note: the legacy root twin of this field is deprecated for web — do not read it from `this.data`.
   */
  get monthlyStartAt() {
    return this.contractView.monthlyStartAt;
  }

  /**
   * Get user's phone number.
   * @return {string}
   */
  get phoneNumber() {
    return this.data.phoneNumber;
  }

  /**
   * Get user's subscribed plan id.
   * @return {string}
   * Note: the legacy root twin of this field is deprecated for web — do not read it from `this.data`.
   */
  get planId() {
    return this.contractView.planId;
  }

  /**
   * Get user's post payment form submitted date.
   * @return {Object}
   * Note: the legacy root twin of this field is deprecated for web — do not read it from `this.data`.
   */
  get postPaymentFormSubmittedAt() {
    return this.coachData.postPaymentFormSubmittedAt;
  }

  /**
   * Get user's suscribed product id.
   * @return {string}
   * BRIDGE until phase D (mirrors the server's getActiveProductIds): an empty
   * `contracts` map means an unmigrated doc — pre-contract-era users, and the
   * backfill's skipped no-current/multi-current cases — so fall back to legacy
   * root `product`, which every subscription writer keeps fresh through
   * release one. Without this, feature gates that default to false (CHAT)
   * silently switch off for those users. When the map has ANY entry, the
   * contract view rules and root is never read.
   */
  get product() {
    if (!this.hasContractEntries) {
      return this.data.product;
    }
    return this.contractView.productId;
  }

  /**
   * Get user's assigned roles.
   * @return {Array<String>}
   */
  get roles() {
    return this.data?.roles || [];
  }

  /**
   * Get user's service start date (when user's contract starts).
   * @return {Date}
   * Note: the legacy root twin of this field is deprecated for web — do not read it from `this.data`.
   */
  get serviceStartAt() {
    return this.contractView.serviceStartAt?.toDate();
  }

  get firstServiceStartAt() {
    return getFirstServiceStartAt(this.data.contracts);
  }

  /**
   * Get user's stream token.
   * This is used to authenticate the user with the chat server (stream).
   * @return {string}
   */
  get streamToken() {
    return this.data.streamToken;
  }

  /**
   * Get user's subscription status.
   * This maps to the Stripe subscription status (in uppercase).
   * @return {string}
   * Note: the legacy root twin of this field is deprecated for web — do not read it from `this.data`.
   */
  get subscriptionStatus() {
    return this.flags.subscriptionStatus;
  }

  /**
   * Get user's tags.
   * @return {Array<String>}
   * Note: the legacy root twin of this field is deprecated for web — do not read it from `this.data`.
   */
  get tags() {
    return this.coachData.tags || [];
  }

  /**
   * Get id of user who last interacted with the client
   * @return {string}
   * Note: the legacy root twin of this field is deprecated for web — do not read it from `this.data`.
   */
  get lastInteractionBy() {
    return this.coachData.lastInteractionBy;
  }

  /**
   * Get name of user who last interacted with the client
   * @return {string}
   * Note: the legacy root twin of this field is deprecated for web — do not read it from `this.data`.
   */
  get lastInteractionByUserName() {
    return this.coachData.lastInteractionByUserName;
  }

  /**
  * Get the date of user's last assigned workout
  * @return {Date|void}
  */
  // Note: the legacy root twin of this field is deprecated for web — do not read it from `this.data`.
  get lastWorkoutAssignedDate() {
    // Raw entry Timestamp on purpose: ActivityContent calls .toDate() on it and
    // disengagement.js runs it through toJsDate (which absorbs Timestamps) —
    // master's `?.toDate()` variant only existed because its ActivityContent
    // still read the root via `.data`.
    return this.coachData.lastWorkoutAssignedDate;
  }

  // Computed properties

  /**
   * Get user's user category.
   * @type {UserCategory}
   * @return {string}
   * Note: the legacy root twin of this field is deprecated for web — do not read it from `this.data`.
   */
  get userCategory() {
    return this.coachData.userCategory;
  }

  /**
   * Get user's age
   * return {number|void}
   */
  get age() {
    return this.birthdate && moment().diff(moment(this.birthdate), 'years');
  }

  /**
   * Get user's name (first name and last name).
   * If user has last name, it will be concatenated with first name.
   * If user doesn't have last name, only first name will be returned.
   * @return {string}
   */
  get name() {
    const firstName = (this.firstName || '').trim();
    const lastName = (this.lastName || '').trim();
    return [firstName, lastName].join(' ');
  }

  get statusText() {
    const userDoc = this;
    if (!userDoc) {
      return '-';
    }
    const { status, associatedDateString } = getUserStatus(userDoc, DateFormat.DEFAULT_DATE_FORMAT);
    let statusText = status;
    if (associatedDateString) {
      statusText += ` (${associatedDateString})`;
    }
    return statusText.charAt(0).toUpperCase() + statusText.slice(1);
  }

  // Note: the legacy root twin of this field is deprecated for web — do not read it from `this.data`.
  get userPod() {
    return this.coachData.podId;
  }

  /**
   * Get the user's Slack member id, when their account is linked to Slack.
   * @return {string|undefined}
   */
  get slackId() {
    return this.data.slackId;
  }

  /**
   * Get user's liaison status.
   * @return {boolean}
   */
  get isLiaison() {
    return !!this.data?.isLiaison;
  }

  get leadId() {
    return this.data?.leadId;
  }

  get leadName() {
    return this.data?.leadName;
  }

  // Methods

  /**
   * Creates a new user entry in the database with the provided userId and userData
   * @param {string} userId The user id of the user record to be created
   * @param {object} userData The user data
   * @param {string} userData.email The user email
   * @param {string} [userData.firstName] The user firstName
   * @param {string} [userData.lastName] The user lastName
   * @param {string} [userData.phoneNumber] The user phoneNumber
   * @return {Promise<void>}
   */
  static async createUser(userId, userData) {
    const userDoc = new User(format(firestorePaths.USER_DOC, {
      [pathPlaceholder.USER_ID]: userId,
    }));
    await userDoc.set({
      ...userData,
      createdAt: new Date(),
    }, {
      merge: true,
    });
  }

  /**
   * Get all active users by coach id.
   * @param {string} coachId - The coach id.
   * @return {Promise<Collection>} The collection of active users.
   */
  static async getActiveUsersByCoach(coachId) {
    const userCollection = new Collection(firestorePaths.USER, {
      createDocument: (source, options) => (
        new User(source, { ...options, disableObserverRefCount: true, viewAsCoachId: coachId })
      ),
      query: (query) => query
        .where(`coachData.${coachId}.flags.isActive`, '==', true)
      ,
    });
    await User.initCollection(userCollection);
    return userCollection;
  }

  /**
   * Get all active users.
   * Coach-agnostic query — root `flags.isActive` is the cross-coach rollup
   * derived by the server trigger, which is the v2 answer for "active with
   * any coach".
   * @return {Promise<Collection>} The collection of active users.
   */
  static async getAllActiveUsers() {
    const userCollection = new Collection(firestorePaths.USER, {
      createDocument: (source, options) => new User(source, { ...options, disableObserverRefCount: true }),
      query: (query) => query
        .where('flags.isActive', '==', true)
      ,
    });
    await User.initCollection(userCollection);
    return userCollection;
  }

  /**
   * Get user by id.
   * @param {string} id - The user id.
   * @param {object} [opts] - User constructor options (e.g. viewAsCoachId).
   * @return {Promise<User>} The user document.
   */
  static async getById(id, opts) {
    const userDoc = new User(format(firestorePaths.USER_DOC, {
      [pathPlaceholder.USER_ID]: id,
    }), opts);
    await userDoc.init();
    return userDoc.exists ? userDoc : null;
  }

  /**
   * The `coachData.{coachId}` field path prefix for per-coach writes.
   * Throws when no coach context could be resolved, so a write can never
   * land on `coachData.undefined`.
   * @return {string}
   */
  get coachDataPath() {
    if (!this.resolvedCoachId) {
      throw new Error(`Cannot write coach-scoped data for user ${this.id}: no coach context`);
    }
    return `coachData.${this.resolvedCoachId}`;
  }

  /**
   * Update user's notes from the coach.
   * Web/server-only field — written to `coachData.{coachId}` only (no root twin).
   *
   * By default this overwrites `coachNotes` with `notes` in full (used by the
   * Notes editor's Save action, which always has the complete edited value).
   * Pass `{ append: true }` to instead atomically concatenate `notes` onto the
   * current server value via a Firestore transaction, so two "Add to Notes"
   * chat-highlight appends in a row (or one racing the Notes editor's
   * autosave) read the server's current value at write time instead of a
   * stale locally-cached one.
   *
   * @param {string} notes - The new coach notes, or the HTML fragment to append.
   * @param {string} editorDisplayName - Name of the coach saving the notes (stored for audit).
   * @param {{ append?: boolean }} [options]
   * @return {Promise<void>}
   */
  updateCoachNotes = async (notes, editorDisplayName, { append = false } = {}) => {
    if (!append) {
      await this.updateFields({
        [`${this.coachDataPath}.coachNotes`]: notes,
        [`${this.coachDataPath}.coachNotesUpdatedAt`]: getFirebase().firestore.FieldValue.serverTimestamp(),
        [`${this.coachDataPath}.coachNotesUpdatedByName`]: editorDisplayName || '',
      });
      return;
    }

    const db = getFirebase().default.firestore();
    const coachId = this.resolvedCoachId;
    await db.runTransaction(async (transaction) => {
      const snap = await transaction.get(this.ref);
      const current = snap.data()?.coachData?.[coachId]?.coachNotes || '';
      transaction.update(this.ref, {
        [`${this.coachDataPath}.coachNotes`]: `${current}${notes}`,
        [`${this.coachDataPath}.coachNotesUpdatedAt`]: getFirebase().firestore.FieldValue.serverTimestamp(),
        [`${this.coachDataPath}.coachNotesUpdatedByName`]: editorDisplayName || '',
      });
    });
  };

  /**
   * Update the client's workout preferences (LLM-read restrictions/dislikes).
   * Web/server-only field — written to `coachData.{coachId}` only (no root twin).
   *
   * By default this overwrites `workoutPreferences` with `preferences` in full
   * (used by the Notes editor's Save action, which always has the complete
   * edited value). Pass `{ append: true }` to instead atomically concatenate
   * `preferences` onto the current server value via a Firestore transaction,
   * so concurrent writers (e.g. the Notes editor's autosave vs.
   * AddToNotesMenu's chat-highlight append) cannot clobber each other.
   *
   * Known limitation: if the Notes editor is mid-edit with a pending debounce,
   * a later editor flush can still overwrite the appended text. The transaction
   * only protects against concurrent persisted writes, not against editor
   * local state that hasn't synced yet.
   *
   * @param {string} preferences - The new workout preferences, or the HTML fragment to append.
   * @param {string} editorDisplayName - Name of the coach saving (stored for audit).
   * @param {{ append?: boolean }} [options]
   * @return {Promise<void>}
   */
  updateWorkoutPreferences = async (preferences, editorDisplayName, { append = false } = {}) => {
    if (!append) {
      await this.updateFields({
        [`${this.coachDataPath}.workoutPreferences`]: preferences,
        [`${this.coachDataPath}.workoutPreferencesUpdatedAt`]: getFirebase().firestore.FieldValue.serverTimestamp(),
        [`${this.coachDataPath}.workoutPreferencesUpdatedByName`]: editorDisplayName || '',
      });
      return;
    }

    const db = getFirebase().default.firestore();
    const coachId = this.resolvedCoachId;
    await db.runTransaction(async (transaction) => {
      const snap = await transaction.get(this.ref);
      const current = snap.data()?.coachData?.[coachId]?.workoutPreferences || '';
      transaction.update(this.ref, {
        [`${this.coachDataPath}.workoutPreferences`]: `${current}${preferences}`,
        [`${this.coachDataPath}.workoutPreferencesUpdatedAt`]: getFirebase().firestore.FieldValue.serverTimestamp(),
        [`${this.coachDataPath}.workoutPreferencesUpdatedByName`]: editorDisplayName || '',
      });
    });
  };

  /**
   * Update the client's custom tags for the viewed coach.
   * Web/server-only field — written to `coachData.{coachId}` only (no root twin).
   * Also clears the chosen check-in template when its tag is no longer on the client, in the same write.
   * @param {Array<string>} tags - The full custom tags list.
   * @return {Promise<void>}
   */
  updateCustomTags = (tags) => {
    const chosenTemplateTag = this.checkInTemplateTag;
    const isChosenTemplateGone = !!chosenTemplateTag
      && chosenTemplateTag !== DEFAULT_TAG
      && !tags.includes(chosenTemplateTag);

    return this.updateFields({
      [`${this.coachDataPath}.customTags`]: tags,
      ...(isChosenTemplateGone && {
        [`${this.coachDataPath}.checkInTemplateTag`]: getFirebase().firestore.FieldValue.delete(),
      }),
    });
  };

  /**
   * Choose which check-in template the client receives from the viewed coach.
   * Web/server-only field — written to `coachData.{coachId}` only (no root twin).
   * @param {string} tag - A template tag, or `default` for the coach's default template.
   * @return {Promise<void>}
   */
  updateCheckInTemplateTag = (tag) => (
    this.updateFields({ [`${this.coachDataPath}.checkInTemplateTag`]: tag })
  );

  /**
   * Update user's createdAt date if it's not already set.
   * @param {string} createdAt - The new createdAt date.
   * @return {Promise<void>}
   */
  updateCreatedAt = async (createdAt) => {
    /*
      Update the userDoc creation time, but only if it's not already set, in order to
      allow us to test overriding it to a different value.
    */
    if (!this.data.createdAt) {
      await this.set({ createdAt: new Date(createdAt) }, { merge: true });
    }
  };

  /**
   * Update IS user's on-call status.
   * Also clears any pending auto-restore marker (set by the backend when it auto-disables
   * Also clears any pending auto-restore marker (set by the backend when it auto-disables
   * on-call after the last tab record goes away — hide, close, or disconnect), so an
   * explicit write always supersedes a pending auto-restore.
   * @param {boolean} isISOnCall - If IS lead is on call or not.
   * @return {Promise<void>}
   */
  updateIsISOnCall = async (isISOnCall) => {
    await this.updateFields({
      isISOnCall,
      shouldAutomaticallyEnableIsOnCall: getFirebase().firestore.FieldValue.delete(),
    });
  };

  /**
   * Update user's meal plan notification resume date.
   * Notification control — written to `coachData.{coachId}` only (no root twin).
   * @param {string} date - The meal plan notification resume date.
   * @return {Promise<void>}
   */
  updateMealPlanNotificationsResumeDate = async (date) => {
    await this.updateFields({ [`${this.coachDataPath}.mealPlanNotificationsResumeDate`]: date });
  };

  /**
   * Update user's workout notification resume date.
   * Notification control — written to `coachData.{coachId}` only (no root twin).
   * @param {string} date - The workout notification resume date.
   * @return {Promise<void>}
   */
  updateWorkoutNotificationsResumeDate = async (date) => {
    await this.updateFields({ [`${this.coachDataPath}.workoutNotificationsResumeDate`]: date });
  };

  /**
   * Set or clear the disengagement/broadcast silence window. Map-only — the
   * getter above and every server consumer (disengagement notification,
   * re-evaluation, disengaged recipients, broadcast send gate) read the coach
   * entry; the legacy root twin is dead for this field.
   * @param {Date|firebase.firestore.FieldValue} value - Silence-until date, or a delete sentinel to clear.
   * @return {Promise<void>}
   */
  updateDisengagementBroadcastSilencedUntil = async (value) => {
    await this.updateFields({ [`${this.coachDataPath}.disengagementBroadcastSilencedUntil`]: value });
  };

  /**
   * activateUser
   * This is used to force the activation process on a user doc.
   * One atomic update: per-coach state, per-contract episode state and the
   * mobile-read legacy root twins. The server rollup trigger derives the
   * root `flags.*` twins from `coachData` — web no longer writes them.
   *
   * @param {string} activatedBy - The user id of the user who activated the user.
   * @param {UserContract} userContractDoc - The contract being (re)activated,
   * used as the `contracts.{contractId}` key and for the coach context.
   * Required — without it the `contracts.{contractId}` entry would be silently
   * skipped, leaving the contract view stale.
   * @returns {Promise<void>}
   */
  activateUser = (activatedBy, userContractDoc) => {
    const now = new Date();

    if (!userContractDoc?.id) {
      throw new Error(`Cannot activate user ${this.id}: no contract context`);
    }

    const coachId = userContractDoc.coach || this.resolvedCoachId;
    const contractId = userContractDoc.id;

    if (!coachId) {
      throw new Error(`Cannot activate user ${this.id}: no coach context`);
    }

    // Only delete cancelAt if it exists and is in the past
    const shouldDeleteCancelAt = this.cancelAt && moment.unix(this.cancelAt).isBefore(moment());
    const deleteValue = getFirebase().firestore.FieldValue.delete();

    return this.updateFields({
      // Per-coach relationship state.
      [`coachData.${coachId}.flags.isActive`]: true,
      [`coachData.${coachId}.flags.subscriptionStatus`]: ClientStatus.ACTIVE,
      /*
        Set the first payment successful flag to true to avoid showing a payment failure message in the mobile app,
        which blocks all other functions.
      */
      [`coachData.${coachId}.flags.firstPaymentSuccessful`]: true,
      // Workout and meal plan notifications should be resumed immediately if they were not manually paused before.
      ...(this.mealPlanNotificationsResumeDate === this.serviceStartAt
        && { [`coachData.${coachId}.mealPlanNotificationsResumeDate`]: now }),
      ...(this.workoutNotificationsResumeDate === this.serviceStartAt
        && { [`coachData.${coachId}.workoutNotificationsResumeDate`]: now }),
      // Per-contract episode state.
      [`contracts.${contractId}.coachId`]: coachId,
      [`contracts.${contractId}.productId`]: userContractDoc.productId,
      [`contracts.${contractId}.isActive`]: true,
      [`contracts.${contractId}.subscriptionStatus`]: ClientStatus.ACTIVE,
      [`contracts.${contractId}.serviceStartAt`]: now,
      [`contracts.${contractId}.breakEndDate`]: now,
      [`contracts.${contractId}.activatedBy`]: activatedBy,
      [`contracts.${contractId}.activatedManuallyAt`]: now,
      ...(shouldDeleteCancelAt && { [`contracts.${contractId}.cancelAt`]: deleteValue }),
      // Mobile-read legacy root twins (old app builds still gate on these).
      isOnboarded: true, // Cancelled users should be marked as onboarded once again.
      serviceStartAt: now, // Set the service start date to now so that the user can use the app right away.
      breakEndDate: now,
      activatedBy,
      activatedManuallyAt: now, // Save when we activated the user manually for traceability.
      ...(shouldDeleteCancelAt && { cancelAt: deleteValue }),
    });
  };

  /**
   * Get all users by coach id.
   * @param {string} coachId - The coach id.
   * @return {Promise<Collection>} The collection of users.
   */
  static async getUsersByCoach(coachId) {
    const userCollection = new Collection(firestorePaths.USER, {
      createDocument: (source, options) => (
        new User(source, { ...options, disableObserverRefCount: true, viewAsCoachId: coachId })
      ),
      query: (query) => query
        .where('assignedCoaches', 'array-contains', coachId)
      ,
    });
    await User.initCollection(userCollection);
    return userCollection;
  }

  /**
   * Get all users fro thr given role.
   * @param {string} role - The role of the users.
   * @return {Promise<Collection>} The collection of users.
   */
  static async getUsersByRole(role) {
    const userCollection = new Collection(firestorePaths.USER, {
      createDocument: (source, options) => new User(source, { ...options, disableObserverRefCount: true }),
      query: (query) => query
        .where(UserRoleFlag[role], '==', true)
      ,
    });
    await User.initCollection(userCollection);
    return userCollection;
  }

  /**
   * get user by email
   * @param {string} email - The email of the user.
   * @return {Promise<Collection>} The collection of users.
   */
  static async getUserByEmail(email) {
    const userCollection = new Collection(firestorePaths.USER, {
      createDocument: (source, options) => new User(source, options),
      query: (query) => query
        .where('email', '==', email)
      ,
    });
    await User.initCollection(userCollection);
    return userCollection;
  }

  /**
   * Get all internal users.
   * @return {Promise<Array>} The array of internal users.
   */
  static async getInternalUsers() {
    // get docs from user_claims collection where admin is true
    const db = getFirestore();
    const userClaimsCol = await db.collection(firestorePaths.USER_CLAIMS)
      .where('admin', '==', true)
      .get();
    const userIds = userClaimsCol.docs.map((doc) => doc.id);
    // get users from users collection where id is in the array
    // we could only get 10 at a time, so we need to loop through the array
    const internalUsers = [];

    const promises = [];
    for (let i = 0; i < userIds.length; i += 10) {
      const usersCol = new Collection(firestorePaths.USER, {
        createDocument: (source, options) => new User(source, options),
        query: (query) => query
          // __name__ can be used to query by document id
          .where('__name__', 'in', userIds.slice(i, i + 10))
        ,
      });
      promises.push(User.initCollection(usersCol).then(() => {
        internalUsers.push(...usersCol.docs);
      }));
    }
    await Promise.all(promises);
    return internalUsers;
  }

  /**
   * Add or remove custom permissions for a user.
   * @param {string} selectedFeature - The feature for which permissions are being set.
   * @param {Array<string>} selectedPermissions - The list of permissions to be assigned.
   * @param {boolean} [removePermission=false] - Whether to remove the permissions for the selected feature.
   * @return {Promise<Object>} The updated custom permissions object.
   */
  async setPermissions(selectedFeature, selectedPermissions, removePermission = false) {
    const existingPermissions = this.customPermissions || {};

    const updatedPermissions = { ...existingPermissions };

    if (removePermission) {
      delete updatedPermissions[selectedFeature];
    } else {
      updatedPermissions[selectedFeature] = selectedPermissions;
    }

    await this.update({ customPermissions: updatedPermissions });
    this.data.customPermissions = updatedPermissions;
  }

  /**
   * Add or remove role for a user.
   * @param {string} roleName - The feature for which permissions are being set.
   * @param {boolean} [removeRole=false] - Whether to remove the role for the selected user.
   * @return {Promise<Object>} The updated custom permissions object.
   */
  async setRoles(roleName, removeRole = false) {
    const existingRoles = this.roles || [];

    let updatedRoles;

    if (removeRole) {
      updatedRoles = existingRoles.filter((role) => role !== roleName);
    } else {
      updatedRoles = [...existingRoles, roleName];
    }

    /*
     * `roles` only. The paired boolean cannot be written here: `firestore.rules` allows `roles`
     * under `isSuperUser` and the flags under `userIsAdmin`, each via `editOnlyChangesFields`, so
     * an update touching both matches neither branch and is rejected outright. Keeping the two in
     * step is the `updateUserPermissions` trigger's job, which runs with admin privileges.
     */
    await this.update({ roles: updatedRoles });
    this.data.roles = updatedRoles;
  }

  /**
   * Get users assigned to a pod. Pods always have coach context, so pod
   * membership lives in `coachData.{coachId}.podId`.
   * @param {string} podId - The pod id.
   * @param {string} coachId - The coach the pod belongs to.
   * @return {Promise<Collection>} The collection of users in the pod.
   */
  static async getUserByPodId(podId, coachId) {
    const userCollection = new Collection(firestorePaths.USER, {
      createDocument: (source, options) => new User(source, { ...options, viewAsCoachId: coachId }),
      query: (query) => query
        .where(`coachData.${coachId}.podId`, '==', podId)
      ,
    });
    await User.initCollection(userCollection);
    return userCollection;
  }
}

export default User;

export {
  UserRole,
  UserRoleFlag,
  UserCategory,
};
